/**
 * appointments / scheduling / deposits response PROJECTIONS — hand-composed from
 * generated atoms (see ./leads.ts for the pattern).
 *
 * Covers three related response surfaces:
 *  - appointments: entity, detail-with-relations, list wrapper.
 *  - deposits: entity, list wrapper, create/refund action responses.
 *  - scheduling: blocked-time (+ occurrence-expanded), blocked-time-types,
 *    time-off, shifts, resolved-shift-days (computed), wage config.
 *
 * All dates/times are ISO strings on the wire (the atoms are already wire-shaped).
 * Availability/slot and resolved-shift responses are COMPUTED (not tables), so
 * they are hand-written `z.object` projections, not atoms.
 */
import { appointmentStatusValues, leadSourceValues, } from '@borradh-workspace/labels';
import { z } from 'zod';
import { appointmentAtomSchema, appointmentDepositAtomSchema, appointmentServiceAtomSchema, blockedTimeAtomSchema, blockedTimeTypeAtomSchema, practitionerWageConfigAtomSchema, shiftAtomSchema, timeOffAtomSchema, } from '../generated/index.js';
import { importRowErrorSchema } from './catalog.js';
import { appointmentReportSourceSchema } from './reports.js';
// ============================================================================
// APPOINTMENTS
// ============================================================================
/**
 * The appointment entity as stored — the atom verbatim, exposed under a contract
 * name so consumers depend on the contract, not the generated file.
 */
export const appointmentSchema = appointmentAtomSchema;
/** Joined lead summary on an appointment (subset of the lead row). */
export const appointmentLeadSchema = z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    // Typed jsonb columns — hand-modeled here (atoms carry these as z.unknown()).
    metadata: z.record(z.string(), z.unknown()).nullable().optional(),
    formData: z.record(z.string(), z.unknown()).nullable().optional(),
});
/** Joined assigned-user summary on an appointment. */
export const appointmentAssignedToSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    image: z.string().nullable(),
});
/**
 * Joined PRIMARY-service summary on an appointment (the catalog row that
 * `appointment.serviceId` points at — line item 0). Distinct from the cart line
 * items below, which are `appointment_service` rows.
 */
export const appointmentPrimaryServiceSchema = z.object({
    id: z.string(),
    name: z.string(),
    priceText: z.string().nullable(),
    appointmentDuration: z.number().nullable(),
    // NOTE: deliberately NOT `turnaroundMinutes`. This is a narrow display
    // summary of the joined catalog row; the query that builds it selects only
    // these columns, and turnaround is scheduling internals nothing here renders.
    // Adding it made every endpoint returning this projection fail response-
    // contract validation with a 500 — the clients list among them.
});
/**
 * One cart line item — an `appointment_service` row verbatim (the generated
 * atom). The full cart is `services[]` on the appointment-with-relations
 * projection below.
 */
export const appointmentServiceSchema = appointmentServiceAtomSchema;
/**
 * The running cart total for an appointment. `isExact` is true only when every
 * line carries a price; otherwise `totalCents` is null and `fromCents` is the
 * sum of the known lines (Fresha's "from £X"). Mirrors `computeCartTotal` in
 * @borradh-workspace/features.
 */
export const appointmentCartTotalSchema = z.object({
    totalCents: z.number().nullable(),
    isExact: z.boolean(),
    fromCents: z.number().nullable(),
});
/**
 * The effective deposit summary attached to an appointment — the paid one if
 * present, else the latest. A trimmed projection of the deposit atom (status +
 * amount + paidAt) for the calendar's deposit badge / "Deposit paid €X" row.
 */
export const appointmentDepositSummarySchema = z.object({
    status: appointmentDepositAtomSchema.shape.status,
    amountCents: appointmentDepositAtomSchema.shape.amountCents.nullable(),
    currency: appointmentDepositAtomSchema.shape.currency,
    paidAt: appointmentDepositAtomSchema.shape.paidAt,
    refundedAmountCents: z.number().int().nonnegative().optional(),
    refundPendingAmountCents: z.number().int().nonnegative().optional(),
    forfeitedAmountCents: z.number().int().nonnegative().optional(),
    isGroupAllocation: z.literal(true).optional(),
    reconciliationRequired: z.literal(true).optional(),
});
/**
 * `GET /appointments/:id` and list items — the appointment atom extended with
 * its (optional) joined lead, assigned user, primary service, and the full
 * multi-service cart (`services`).
 */
export const appointmentWithRelationsSchema = appointmentAtomSchema.extend({
    lead: appointmentLeadSchema.optional(),
    /**
     * NULLABLE, not merely optional.
     *
     * `appointment.assignedToId` is `onDelete: 'set null'` BY DESIGN — its schema
     * comment says removing a staff member must not cascade-delete their
     * (possibly historical/financial) appointments. So every appointment that
     * belonged to a departed staff member carries `assignedToId = NULL`, and
     * Drizzle's `with: { assignedTo: … }` then returns `assignedTo: null`.
     *
     * Declared `.optional()` only, parsing ANY such row threw — and it takes
     * down the WHOLE list response, not the one row. That is every organization
     * which has ever removed a team member. `service` and `deposit` two lines
     * below were already `.nullable().optional()`; `assignedTo` was the outlier.
     *
     * Found by executing the tool against a real Postgres row holding a real
     * NULL (`_integration/tool-appointments.int-spec.ts`). No static gate could
     * have found it: the GENERATED atom anchors column names, but `assignedTo`
     * is a JOIN, so its nullability was nobody's belief but the schema author's.
     */
    assignedTo: appointmentAssignedToSchema.nullable().optional(),
    service: appointmentPrimaryServiceSchema.nullable().optional(),
    services: z.array(appointmentServiceSchema).optional(),
    deposit: appointmentDepositSummarySchema.nullable().optional(),
});
/**
 * Why a reschedule did or did not tell the customer. `sent` is the only
 * outcome where the customer was emailed: it is reported from the send itself,
 * after the update commits, not predicted from the customer record.
 */
export const rescheduleNotificationReasonValues = [
    'sent',
    'not_requested',
    'no_customer',
    'no_email_address',
    'send_failed',
];
/**
 * What `PUT /appointments/:id` did about telling the customer, present only
 * when the update moved the appointment. Claire reports exactly this (ENG-2033):
 * before it existed she assumed an email went out to a lead with no address.
 */
export const rescheduleNotificationSchema = z.object({
    sent: z.boolean(),
    channel: z.enum(['email']).nullable(),
    reason: z.enum(rescheduleNotificationReasonValues),
});
/** `PUT /appointments/:id` — the updated row plus the notification outcome. */
export const updateAppointmentResponseSchema = appointmentAtomSchema.extend({
    rescheduleNotification: rescheduleNotificationSchema.optional(),
});
/**
 * `GET /appointments?summary=true` — counts over every matching appointment,
 * not just the returned page, so "how many" never means counting rows.
 */
export const listAppointmentsSummarySchema = z.object({
    total: z.number(),
    byStatus: z.array(z.object({ status: z.enum(appointmentStatusValues), count: z.number() })),
    byService: z.array(z.object({
        serviceId: z.string().nullable(),
        serviceName: z.string().nullable(),
        count: z.number(),
    })),
    byBookedVia: z.array(z.object({ bookedVia: appointmentReportSourceSchema, count: z.number() })),
    byLeadSource: z.array(z.object({ leadSource: z.enum(leadSourceValues), count: z.number() })),
});
/** `GET /appointments` — `{ items, total, limit, offset }` list wrapper. */
export const listAppointmentsResponseSchema = z.object({
    items: z.array(appointmentWithRelationsSchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    summary: listAppointmentsSummarySchema.optional(),
});
/** `POST /appointments/import-csv` — the import outcome summary. */
export const importCalendarCsvResponseSchema = z.object({
    appointmentsCreated: z.number(),
    appointmentsUpdated: z.number(),
    blockedTimesCreated: z.number(),
    shiftsCreated: z.number(),
    customersCreated: z.number(),
    /** Bookings written over an existing active booking for the same staff member. */
    doubleBooked: z.number(),
    skipped: z.number(),
    errors: z.array(importRowErrorSchema),
    rowsInFile: z.number(),
    cleanedRows: z.number(),
    skippedRows: z.number(),
    columnMapping: z.record(z.string(), z.string()),
});
// ============================================================================
// MANAGE BOOKING (public, token-authenticated — patient self-serve)
// ============================================================================
/**
 * The org's cancellation terms, as shown to the patient on the manage page.
 *
 * REVISED PRODUCT DECISION (docs/plans/cancellation-policy.md): a late online
 * CANCEL is now ALLOWED (`canCancel` stays true), gated by a forfeit-warning
 * dialog the frontend shows when `cancelForfeitsDeposit` is true AND the
 * booking carries a paid deposit (`ManagedAppointment.deposit`). A late online
 * RESCHEDULE is still blocked outright — `blockedReason` carries the VERBATIM
 * customer-facing message whenever NEITHER action is available. The two can be
 * blocked for different reasons (cancellation switched off AND too late to
 * move), in which case both messages are joined into it.
 */
export const cancellationPolicySchema = z.object({
    /** The ONE deadline governing both online cancel and online reschedule. */
    deadlineHours: z.number(),
    isWithinFreeWindow: z.boolean(),
    /** Last instant still inside the free window (ISO); null when there is none. */
    freeUntil: z.string().nullable(),
    /** True even once late — a late cancel is allowed, not blocked. */
    canCancel: z.boolean(),
    canReschedule: z.boolean(),
    /**
     * True only for a LATE cancel where the org's `depositForfeitOnLateCancel`
     * switch forfeits the deposit instead of refunding it. Combine with
     * `ManagedAppointment.deposit` (present + `status: 'paid'`) to decide
     * whether to show the forfeit-warning dialog before confirming a cancel.
     */
    cancelForfeitsDeposit: z.boolean(),
    blockedReason: z.string().nullable(),
});
/**
 * `GET /public/booking/:slug/manage/:token`.
 *
 * COMPUTED, and deliberately NOT the appointment atom. The token proves "I hold
 * this booking's link", which is weaker than "I am this client" — so this
 * projection exposes only what the holder already knows (what, when, with whom)
 * and never the lead record, internal notes, or org-internal appointment fields.
 * Widening it to `appointmentAtomSchema` would leak all three to an anonymous
 * caller.
 */
export const managedAppointmentSchema = z.object({
    /** Always true; kept for older mobile builds that still read it. */
    bookingSystemEnabled: z.boolean().optional(),
    appointmentId: z.string(),
    title: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    status: z.string(),
    /** False once the booking is terminal (completed/cancelled/no-show). */
    isActionable: z.boolean(),
    serviceName: z.string().nullable(),
    practitionerName: z.string().nullable(),
    /** Drive the reschedule picker off the same public slots endpoint. */
    serviceId: z.string().nullable(),
    practitionerId: z.string().nullable(),
    /** The BOOKED duration, not the service's current one. */
    durationMinutes: z.number(),
    visit: z
        .object({
        startTime: z.string(),
        endTime: z.string(),
        durationMinutes: z.number(),
        totalPriceCents: z.number().nullable(),
        payment: z
            .object({
            status: appointmentDepositAtomSchema.shape.status,
            amountCents: z.number().nullable(),
            currency: z.string(),
            refundedAmountCents: z.number().int().nonnegative().optional(),
            refundPendingAmountCents: z.number().int().nonnegative().optional(),
            reconciliationRequired: z.literal(true).optional(),
        })
            .optional(),
        services: z.array(z.object({
            appointmentId: z.string(),
            serviceName: z.string(),
            practitionerName: z.string().nullable(),
            startTime: z.string(),
            endTime: z.string(),
            durationMinutes: z.number(),
            priceCents: z.number().nullable(),
            status: z.string(),
        })),
    })
        .optional(),
    organization: z.object({
        name: z.string(),
        slug: z.string(),
        logo: z.string().nullable(),
        timezone: z.string(),
        /** Booking window for the reschedule picker. */
        bookingMinDaysAhead: z.number().optional(),
        bookingMaxDaysAhead: z.number().optional(),
    }),
    /**
     * The deposit at stake on this booking, if any — what the forfeit-warning
     * dialog needs (see `policy.cancelForfeitsDeposit`). Null when there is no
     * deposit; the UI must not show a forfeit warning in that case.
     */
    deposit: appointmentDepositSummarySchema.omit({ paidAt: true }).nullable(),
    policy: cancellationPolicySchema,
});
/**
 * `POST /public/booking/:slug/manage/:token/cancel`.
 *
 * A late cancel is now ALLOWED (see `cancellationPolicySchema`), so unlike
 * before this can report EITHER outcome per
 * docs/plans/cancellation-policy.md §9: `depositRefunded` for an early cancel
 * (or a late one the org has chosen not to forfeit), `depositForfeited` for a
 * late cancel the org forfeits. At most one of the two is ever true.
 */
export const cancelManagedAppointmentResponseSchema = z.object({
    appointmentId: z.string(),
    /** Whether a deposit was released (refunded or its pending request cancelled). */
    depositRefunded: z.boolean(),
    /** Whether a paid deposit was forfeited (late cancel, org forfeits). */
    depositForfeited: z.boolean(),
});
/** `POST /public/booking/:slug/manage/:token/reschedule`. */
export const rescheduleManagedAppointmentResponseSchema = z.object({
    appointmentId: z.string(),
    startDate: z.string(),
    endDate: z.string(),
});
// ============================================================================
// DEPOSITS
// ============================================================================
/** The appointment-deposit entity — the atom verbatim. */
export const depositSchema = appointmentDepositAtomSchema;
/** `GET /deposits` — list wrapper. */
export const listDepositsResponseSchema = z.object({
    items: z.array(depositSchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
});
/**
 * `POST /deposits` — a policy-priced deposit payment link (ENG-1306), or
 * `not_required` when the clinic's policy asks for nothing online. `expiresAt`
 * is both the link's and the slot hold's expiry.
 */
export const createDepositResponseSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('created'),
        deposit: depositSchema,
        checkoutUrl: z.string(),
        amountCents: z.number().int(),
        currency: z.string(),
        expiresAt: z.string().datetime(),
    }),
    z.object({ status: z.literal('not_required') }),
]);
/** `POST /deposits/:id/send` — the message carrying the link was accepted. */
export const sendDepositLinkResponseSchema = z.object({
    deposit: depositSchema,
    conversationId: z.string(),
    /** Delivery/read state is read later from this message, never asserted. */
    messageId: z.string(),
});
const depositLinkDeliverySchema = z.discriminatedUnion('state', [
    z.object({ state: z.literal('not_sent') }),
    z.object({
        state: z.enum(['queued', 'sent', 'delivered', 'read']),
        at: z.string().datetime().nullable(),
        messageId: z.string(),
    }),
]);
/** One link attempt as staff see it in the booking panel / history. */
export const depositLinkViewSchema = z.object({
    id: z.string(),
    attempt: z.number().int(),
    status: depositSchema.shape.status,
    amountCents: z.number().int(),
    currency: z.string(),
    checkoutUrl: z.string().nullable(),
    expiresAt: z.string().datetime(),
    source: depositSchema.shape.source,
    createdAt: z.string().datetime(),
    createdByUserId: z.string().nullable(),
    revokedAt: z.string().datetime().nullable(),
    revokedByUserId: z.string().nullable(),
    lastSentAt: z.string().datetime().nullable(),
    sendCount: z.number().int(),
    delivery: depositLinkDeliverySchema,
    /** Paid by a delayed method (e.g. SEPA); the money is still clearing. */
    paymentProcessingAt: z.string().datetime().nullable(),
    paidAt: z.string().datetime().nullable(),
    receiptEmailRequested: z.boolean(),
    receiptSentAt: z.string().datetime().nullable(),
    receiptUrl: z.string().nullable(),
    reviewReason: z.string().nullable(),
    failureReason: z.string().nullable(),
});
/**
 * `GET /deposits/appointment/:appointmentId/link` — what a link would charge
 * (policy-derived, not editable), whether the clinic can charge, and every
 * link attempt for the booking. `canManage` = the caller may create/send/revoke
 * (every staff member of the clinic today; kept so the UI needs no change if
 * that is ever narrowed).
 */
export const depositLinkQuoteSchema = z.object({
    appointmentId: z.string(),
    enabled: z.boolean(),
    canManage: z.boolean(),
    // Fail closed when an older API has not supplied booking eligibility.
    canCreate: z.boolean().default(false),
    required: z.boolean(),
    amountCents: z.number().int(),
    currency: z.string().nullable(),
    reason: z.enum(['none', 'deposit', 'full', 'mixed']),
    chargeable: z.discriminatedUnion('ok', [
        z.object({ ok: z.literal(true) }),
        z.object({
            ok: z.literal(false),
            reason: z.enum([
                'not_connected',
                'inactive',
                'charges_disabled',
                'unknown',
            ]),
        }),
    ]),
    projectedExpiresAt: z.string().datetime(),
    holdExpirationHours: z.number(),
    receiptEmailAvailable: z.boolean(),
    links: z.array(depositLinkViewSchema),
});
/** Response when refunding a deposit (updated deposit + Stripe refund id). */
export const refundDepositResponseSchema = z.object({
    deposit: depositSchema,
    refundId: z.string(),
});
// ============================================================================
// SCHEDULING — blocked time
// ============================================================================
/** A blocked-time type (preset) — the atom verbatim. */
export const blockedTimeTypeSchema = blockedTimeTypeAtomSchema;
/** A blocked-time series row — the atom verbatim. */
export const blockedTimeSchema = blockedTimeAtomSchema;
/**
 * `GET /blocked-time` — occurrence-EXPANDED blocked times. Recurring series are
 * expanded into per-occurrence rows: `startDate`/`endDate` are the occurrence
 * dates, `blockedTimeId` points at the parent series, and `originalStart`
 * (RECURRENCE-ID, an ISO string on the wire) identifies the occurrence for
 * scope='this'/'following' edits. `practitionerIds` empty = org-wide block.
 */
export const blockedTimeWithPractitionersSchema = blockedTimeAtomSchema.extend({
    practitionerIds: z.array(z.string()),
    blockedTimeId: z.string().optional(),
    originalStart: z.string().datetime().optional(),
});
/** `GET /blocked-time` returns a bare array of expanded occurrences. */
export const listBlockedTimeResponseSchema = z.array(blockedTimeWithPractitionersSchema);
/** `GET /blocked-time-types` returns a bare array of presets. */
export const listBlockedTimeTypesResponseSchema = z.array(blockedTimeTypeSchema);
// ============================================================================
// SCHEDULING — time off
// ============================================================================
/** A time-off series row — the atom verbatim. */
export const timeOffSchema = timeOffAtomSchema;
/** `GET /time-off` returns a bare array of (occurrence-expanded) time-off rows. */
export const listTimeOffResponseSchema = z.array(timeOffSchema);
// ============================================================================
// SCHEDULING — shifts (raw rows + computed resolved days)
// ============================================================================
/** A raw shift row (weekly pattern or date override) — the atom verbatim. */
export const shiftSchema = shiftAtomSchema;
/** One resolved working interval on a resolved shift day. COMPUTED, not a table. */
export const resolvedShiftIntervalSchema = z.object({
    shiftId: z.string(),
    startMinutes: z.number(),
    endMinutes: z.number(),
    locationId: z.string().nullable(),
});
/**
 * `GET /shifts` item — the resolved schedule for one practitioner on one date,
 * with date overrides applied over the weekly pattern. COMPUTED (not a table):
 * `date` is a YYYY-MM-DD string, `dayOfWeek` is 0=Sun..6=Sat.
 */
export const resolvedShiftDaySchema = z.object({
    practitionerId: z.string(),
    date: z.string(),
    dayOfWeek: z.number(),
    isOff: z.boolean(),
    /** `default` = no roster set; opening hours (or 9–5 Mon–Fri) were used. */
    source: z.enum(['weekly', 'override', 'default']),
    intervals: z.array(resolvedShiftIntervalSchema),
});
/** `GET /shifts` returns a bare array of resolved shift days. */
export const listShiftsResponseSchema = z.array(resolvedShiftDaySchema);
// ============================================================================
// SCHEDULING — wage config
// ============================================================================
/** `GET /wage-configs/:practitionerId` — the wage-config atom verbatim. */
export const practitionerWageConfigSchema = practitionerWageConfigAtomSchema;
//# sourceMappingURL=appointments.js.map